import { TranslateService } from "../_services/translate.service";
import { UserService } from "@app/shared/_services/user.service";
import { AuthenticationService } from "@app/shared/_services/authentication.service";
import { combineLatest, iif, of } from "rxjs";
import { availableLanguages, fallBackLanguage } from "@config/config";
import { map } from "rxjs/operators";
import { MockService } from "@app/shared/_services/mock.service";
import { getBrowserLocales } from "@app/shared/_helpers/i18n";
import { User } from "@app/shared/_interfaces/user";
import { environment } from "@environments/environment";

export function setupUserConfig(
  translateService: TranslateService,
  userService: UserService,
  authenticationService: AuthenticationService,
  mockService: MockService,
): Function {
  return () => {
    return new Promise((resolve, reject) => {
      const supportedLanguages = availableLanguages.map(
        (language) => language.id,
      );
      let navigatorLanguage =
        getBrowserLocales({ languageCodeOnly: true })[0] || fallBackLanguage;

      if (!~supportedLanguages.indexOf(navigatorLanguage)) {
        navigatorLanguage = fallBackLanguage;
      }

      return combineLatest([
        of<User.Config>({
          config: {
            fallback_language: fallBackLanguage,
            supported_languages: supportedLanguages,
            current_language: navigatorLanguage,
            timezone: "+00:00",
          },
          userData: null,
          translations: {
            [fallBackLanguage]: mockService.getData("i18n/en"),
            ...(navigatorLanguage !== fallBackLanguage && {
              [navigatorLanguage]: mockService.getData(
                "i18n/" + navigatorLanguage,
              ),
            }),
          },
        }),
        userService.getInfo(),
      ])
        .pipe(
          map(([globalConfig, userData]) => {
            if (userData != null) {
              const [userInfo] = userData;

              if (environment.production) {
                if (userInfo.is_subaccount) {
                  const domain =
                    userInfo.subaccount_config?.dashboard_domain || "self";
                  if (
                    domain !== "self" &&
                    domain !== document.location.hostname
                  ) {
                    throw new Error("wrong_credentials_specified");
                  }
                } else if (userInfo.can_create_subaccount) {
                  const domain = userInfo?.dashboard_domain || "self";
                  if (
                    domain !== "self" &&
                    domain === document.location.hostname
                  ) {
                    throw new Error("wrong_credentials_specified");
                  }
                }
              }

              if (
                environment.production &&
                userInfo.dashboard_layout === "OLD"
              ) {
                throw new Error("old_dashboard_redirect");
              }

              if (
                userInfo.profile_completion_required &&
                !window.location.href.includes("complete-profile") &&
                !window.location.href.includes("myaccount/purchase")
              ) {
                throw new Error("complete_profile_required");
              }

              globalConfig.config = {
                ...globalConfig.config,
                current_language: userInfo.lang,
                timezone: userInfo.timezone,
              };
              if (userInfo.lang !== fallBackLanguage) {
                globalConfig.translations = {
                  ...globalConfig.translations,
                  [userInfo.lang]: mockService.getData(`i18n/${userInfo.lang}`),
                };
              }
              globalConfig.userData = userData;
              authenticationService.toggleCookie(userInfo);
            } else {
              if (authenticationService.isLogged()) {
                throw new Error("config_server_error");
              }
              authenticationService.toggleCookie(null);
            }

            return globalConfig;
          }),
        )
        .subscribe({
          next: (appConfig) => {
            if (appConfig.userData) {
              userService.setUserData(appConfig.userData);
            }

            translateService.reloadByConfig(appConfig);
            resolve(appConfig);
          },
          error: (error) => {
            authenticationService.deauthorizeToken();

            if (error?.message === "old_dashboard_redirect") {
              window.location.href = "https://old.dashboard.serversmtp.com";
            }

            if (error?.message === "wrong_credentials_specified") {
              window.location.href = environment.loginUrl;
            }

            if (error?.message === "config_server_error") {
              window.location.href = environment.loginUrl;
            }

            if (error?.message === "complete_profile_required") {
              window.location.href = "/complete-profile";
            }

            reject(error);
          },
        });
    });
  };
}
